import { useState } from "react";

import { NAV_STARS, type NavStar } from "@nav-games/astro";

/// Equatorial star chart — the 58 navigational stars plotted in their
/// SHA × Dec coordinates. SHA runs left → right from 360° to 0° (so
/// stars in the same celestial-equator slice as the sky's "left" sit
/// on the left of the chart), Dec runs bottom → top from −90° to +90°.
/// Magnitude maps to dot size; click a star to see its catalogue row.
///
/// Two views toggle along the top:
///
///   - "Equatorial" (the default) — the rectangle described above.
///     Easiest to use as a lookup chart: read off SHA and Dec.
///   - "North polar" — an azimuthal-equidistant chart centred on the
///     North Celestial Pole, showing stars with Dec ≥ −15° at radius
///     90° − Dec (so Polaris sits at the centre). Matches what a
///     Northern Hemisphere observer sees overhead at the latitudes
///     the UK Sea-Cadets cover.
///   - "South polar" — same construction centred on the South
///     Celestial Pole, showing stars with Dec ≤ +15°.

interface StarChartProps {
  /// Optional click handler; called with the clicked star's catalogue
  /// entry. The chart also tracks an internal selection for the row
  /// detail strip below it.
  onSelect?: (star: NavStar) => void;
}

type View = "equatorial" | "north-polar" | "south-polar";

const VIEWS: readonly { id: View; label: string }[] = [
  { id: "equatorial", label: "Equatorial" },
  { id: "north-polar", label: "N polar" },
  { id: "south-polar", label: "S polar" },
];

/// Map a star's magnitude to a dot radius in SVG units. Brighter
/// stars get bigger dots; the brightest (Sirius, mag −1.5) is 6 units,
/// the dimmest navigational entries (~mag 3) are 1.5 units.
function dotRadius(magnitude: number): number {
  return Math.max(1.5, 6 - magnitude * 1.1);
}

function starColour(magnitude: number): string {
  if (magnitude < 1) return "#fde047";
  if (magnitude < 2) return "#facc15";
  return "#a3a3a3";
}

const EQ_W = 720;
const EQ_H = 200;
const POLAR_SIZE = 360;

function EquatorialChart({
  onClick,
  selected,
}: {
  onClick: (s: NavStar) => void;
  selected: NavStar | null;
}) {
  const PAD_L = 36;
  const PAD_R = 12;
  const PAD_T = 18;
  const PAD_B = 28;
  const innerW = EQ_W - PAD_L - PAD_R;
  const innerH = EQ_H - PAD_T - PAD_B;
  const x = (sha: number): number => PAD_L + ((360 - sha) / 360) * innerW;
  const y = (dec: number): number => PAD_T + ((90 - dec) / 180) * innerH;

  // SHA gridlines every 30°
  const shaTicks = [0, 30, 60, 90, 120, 150, 180, 210, 240, 270, 300, 330, 360];
  // Dec gridlines every 30°
  const decTicks = [-90, -60, -30, 0, 30, 60, 90];

  return (
    <svg
      viewBox={`0 0 ${EQ_W} ${EQ_H}`}
      style={{ width: "100%", height: "auto", display: "block" }}
      role="img"
      aria-label="Equatorial chart of the 58 navigational stars"
    >
      <rect
        x={PAD_L}
        y={PAD_T}
        width={innerW}
        height={innerH}
        fill="#0b1220"
        stroke="var(--border, #475569)"
      />
      {/* Gridlines */}
      {shaTicks.map((s) => (
        <g key={`sha-${s}`}>
          <line
            x1={x(s)}
            y1={PAD_T}
            x2={x(s)}
            y2={PAD_T + innerH}
            stroke="#1f2937"
            strokeWidth={0.5}
          />
          <text
            x={x(s)}
            y={EQ_H - 8}
            textAnchor="middle"
            fontSize={10}
            fill="var(--text-muted, #94a3b8)"
          >
            {s}°
          </text>
        </g>
      ))}
      {decTicks.map((d) => (
        <g key={`dec-${d}`}>
          <line
            x1={PAD_L}
            y1={y(d)}
            x2={PAD_L + innerW}
            y2={y(d)}
            stroke={d === 0 ? "#475569" : "#1f2937"}
            strokeWidth={d === 0 ? 1 : 0.5}
          />
          <text
            x={PAD_L - 4}
            y={y(d) + 3}
            textAnchor="end"
            fontSize={10}
            fill="var(--text-muted, #94a3b8)"
          >
            {d > 0 ? `+${d}` : d}°
          </text>
        </g>
      ))}
      {/* Axis labels */}
      <text
        x={PAD_L + innerW / 2}
        y={PAD_T - 6}
        textAnchor="middle"
        fontSize={11}
        fill="var(--text-muted)"
      >
        SHA (westward from Aries) →
      </text>
      <text
        x={8}
        y={PAD_T + innerH / 2}
        textAnchor="middle"
        fontSize={11}
        fill="var(--text-muted)"
        transform={`rotate(-90 8 ${PAD_T + innerH / 2})`}
      >
        Dec
      </text>

      {/* Stars */}
      {NAV_STARS.map((star) => {
        const isSelected = selected?.name === star.name;
        return (
          <g
            key={star.name}
            style={{ cursor: "pointer" }}
            onClick={() => onClick(star)}
          >
            <circle
              cx={x(star.shaDeg)}
              cy={y(star.decDeg)}
              r={dotRadius(star.magnitude) + (isSelected ? 4 : 0)}
              fill={starColour(star.magnitude)}
              stroke={isSelected ? "#22d3ee" : "none"}
              strokeWidth={2}
            />
            {/* Label the brightest 18 stars (mag < 1.5) at all zoom levels. */}
            {(star.magnitude < 1.5 || isSelected) && (
              <text
                x={x(star.shaDeg) + dotRadius(star.magnitude) + 3}
                y={y(star.decDeg) + 3}
                fontSize={9}
                fill="#cbd5e1"
              >
                {star.name}
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
}

function PolarChart({
  hemisphere,
  onClick,
  selected,
}: {
  hemisphere: "N" | "S";
  onClick: (s: NavStar) => void;
  selected: NavStar | null;
}) {
  const cx = POLAR_SIZE / 2;
  const cy = POLAR_SIZE / 2;
  const R = POLAR_SIZE / 2 - 16; // map radius (= 105° of dec coverage)
  const maxDec = 105; // show stars within 105° of the pole

  // Project (SHA, Dec) → (x, y) in azimuthal-equidistant.
  function project(sha: number, decDeg: number): { x: number; y: number } | null {
    // Radial distance from pole, in degrees.
    const radDeg = hemisphere === "N" ? 90 - decDeg : 90 + decDeg;
    if (radDeg < 0 || radDeg > maxDec) return null;
    const r = (radDeg / 90) * R;
    // Azimuth around the pole. SHA increases westward; for the
    // northern view we render the celestial sphere as seen from
    // INSIDE looking out (the way the navigator sees it), so SHA
    // wraps clockwise. For the southern view, we flip the chirality
    // by negating the angle.
    const phi =
      hemisphere === "N"
        ? -sha * (Math.PI / 180)
        : sha * (Math.PI / 180);
    return {
      x: cx + r * Math.sin(phi),
      y: cy - r * Math.cos(phi),
    };
  }

  // Dec gridline circles every 30°.
  const decCircles = hemisphere === "N" ? [60, 30, 0, -15] : [-60, -30, 0, 15];

  return (
    <svg
      viewBox={`0 0 ${POLAR_SIZE} ${POLAR_SIZE}`}
      style={{
        width: "100%",
        maxWidth: POLAR_SIZE,
        height: "auto",
        display: "block",
        margin: "0 auto",
      }}
      role="img"
      aria-label={`${hemisphere === "N" ? "North" : "South"}-polar chart of the navigational stars`}
    >
      <circle
        cx={cx}
        cy={cy}
        r={R + 2}
        fill="#0b1220"
        stroke="var(--border, #475569)"
      />
      {decCircles.map((d) => {
        const radDeg = hemisphere === "N" ? 90 - d : 90 + d;
        const r = (radDeg / 90) * R;
        return (
          <g key={`circ-${d}`}>
            <circle
              cx={cx}
              cy={cy}
              r={r}
              fill="none"
              stroke="#1f2937"
              strokeWidth={d === 0 ? 1 : 0.5}
            />
            <text
              x={cx + r + 2}
              y={cy + 3}
              fontSize={9}
              fill="var(--text-muted, #94a3b8)"
            >
              {d > 0 ? `+${d}` : d}°
            </text>
          </g>
        );
      })}
      {/* SHA spokes every 30°. */}
      {[0, 30, 60, 90, 120, 150, 180, 210, 240, 270, 300, 330].map((s) => {
        const p = project(s, hemisphere === "N" ? -15 : 15);
        if (!p) return null;
        return (
          <g key={`spoke-${s}`}>
            <line
              x1={cx}
              y1={cy}
              x2={p.x}
              y2={p.y}
              stroke="#1f2937"
              strokeWidth={0.5}
            />
            <text
              x={p.x + (p.x - cx) * 0.06}
              y={p.y + (p.y - cy) * 0.06 + 4}
              fontSize={9}
              fill="var(--text-muted, #94a3b8)"
              textAnchor="middle"
            >
              {s}°
            </text>
          </g>
        );
      })}
      {/* Stars */}
      {NAV_STARS.map((star) => {
        const p = project(star.shaDeg, star.decDeg);
        if (!p) return null;
        const isSelected = selected?.name === star.name;
        return (
          <g
            key={star.name}
            style={{ cursor: "pointer" }}
            onClick={() => onClick(star)}
          >
            <circle
              cx={p.x}
              cy={p.y}
              r={dotRadius(star.magnitude) + (isSelected ? 4 : 0)}
              fill={starColour(star.magnitude)}
              stroke={isSelected ? "#22d3ee" : "none"}
              strokeWidth={2}
            />
            {(star.magnitude < 1.5 || isSelected) && (
              <text
                x={p.x + dotRadius(star.magnitude) + 3}
                y={p.y + 3}
                fontSize={9}
                fill="#cbd5e1"
              >
                {star.name}
              </text>
            )}
          </g>
        );
      })}
      {/* Pole label */}
      <text
        x={cx}
        y={cy - 6}
        textAnchor="middle"
        fontSize={10}
        fill="var(--text-muted, #94a3b8)"
      >
        {hemisphere === "N" ? "★ NCP" : "★ SCP"}
      </text>
    </svg>
  );
}

export function StarChart({ onSelect }: StarChartProps) {
  const [view, setView] = useState<View>("equatorial");
  const [selected, setSelected] = useState<NavStar | null>(null);

  const handleClick = (s: NavStar) => {
    setSelected(s);
    onSelect?.(s);
  };

  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>Star chart — the 58 navigational stars</h3>

      <div
        style={{
          display: "flex",
          gap: 6,
          marginBottom: 10,
          flexWrap: "wrap",
        }}
      >
        {VIEWS.map((v) => (
          <button
            key={v.id}
            type="button"
            className={view === v.id ? "" : "secondary"}
            onClick={() => setView(v.id)}
            style={{ flex: "1 1 100px", fontSize: 13, padding: "8px 6px" }}
            aria-pressed={view === v.id}
          >
            {v.label}
          </button>
        ))}
      </div>

      <p className="small muted" style={{ margin: "0 0 10px" }}>
        {view === "equatorial"
          ? "All 58 stars plotted in SHA × Dec coordinates. SHA runs right → left (matching the way the celestial sphere drifts past a fixed observer); Dec runs −90° → +90° bottom-to-top. Brighter stars are larger."
          : view === "north-polar"
            ? "Azimuthal-equidistant projection centred on the North Celestial Pole. Polaris sits at the centre; stars within 105° of the pole are plotted. The view a Northern-Hemisphere navigator gets looking up."
            : "Azimuthal-equidistant projection centred on the South Celestial Pole. The view a Southern-Hemisphere navigator gets looking up — Sigma Octantis (faint) marks the pole."}
      </p>

      <div
        style={{
          borderRadius: "var(--radius-sm)",
          overflow: "hidden",
          marginBottom: 10,
        }}
      >
        {view === "equatorial" ? (
          <EquatorialChart onClick={handleClick} selected={selected} />
        ) : (
          <PolarChart
            hemisphere={view === "north-polar" ? "N" : "S"}
            onClick={handleClick}
            selected={selected}
          />
        )}
      </div>

      {selected ? (
        <div
          style={{
            padding: 10,
            borderRadius: "var(--radius-sm)",
            border: "1px solid var(--accent)",
            background: "var(--accent-soft)",
          }}
        >
          <strong>{selected.name}</strong>
          <div className="small muted">
            {selected.constellation} · SHA {selected.shaDeg.toFixed(1)}° · Dec{" "}
            {selected.decDeg >= 0 ? "+" : ""}
            {selected.decDeg.toFixed(1)}° · mag{" "}
            {selected.magnitude.toFixed(1)}
          </div>
        </div>
      ) : (
        <p className="small muted" style={{ margin: 0 }}>
          Click a star to see its catalogue values.
        </p>
      )}
    </div>
  );
}
